import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControlLabel, GuideDot, Radio, RadioGroup } from 'edenred-ui';
import { useTheme } from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { getIsUserImpersonate } from '@epi-selectors/login';
import { LimitType, LUNCH } from '@epi-constants/benefitTypes';
import { ICreateBenefitGroupForm } from '@epi-models/pages/BenefitGroups';
import { FundsLoadingType } from '@epi-shared/components/BenefitGroups/SettingsSections/Lunch/GeneralSettings/Fields/FundsLoadingType';
import { LunchFundsLoadingValues } from '@epi-models/pages/Home';
import { useOnboardingWizard } from '@epi-shared/hooks';

import { BenefitSettingsTooltip } from '../../BenefitSettingsTooltip/BenefitSettingsTooltip';
import { DailyLimitField } from '../../DailyLimitField';

import { GeneralSettingsLoadTypeNotification } from './GeneralSettingsLoadTypeNotification';

interface GeneralSettingsProps {
  maxValue?: number;
  disabled: boolean;
  maxLunchGlobal?: number;
  fundsLoadingType?: LunchFundsLoadingValues;
  canApplyFundsLoadingChangeImmediately: boolean;
  hasScheduledLoads: boolean;
  handleCloseModal: (isOpen: boolean) => void;
}

const GeneralSettings = ({
  maxValue,
  disabled,
  maxLunchGlobal,
  fundsLoadingType,
  canApplyFundsLoadingChangeImmediately,
  hasScheduledLoads,
  handleCloseModal
}: GeneralSettingsProps) => {
  const { fontSemiBold, sizeSmall, gray50, gray85 } = useTheme();
  const { t } = useTranslation();
  const isUserImpersonate = useSelector(getIsUserImpersonate);
  const { watch, setValue, trigger } =
    useFormContext<ICreateBenefitGroupForm>();
  const { isOnBoardingActive } = useOnboardingWizard();
  const spendingLimitFieldValue =
    watch('benefitSettings.lunch.isMaximumSpendingLimit') ?? true;
  const maxAmountPerPeriod = watch('benefitSettings.lunch.maxAmountPerPeriod');
  const [isLoadValueMaximumInitial] = useState(
    spendingLimitFieldValue &&
      (maxLunchGlobal === maxValue || maxLunchGlobal === undefined)
  );
  const [isLoadValueMaximum, setIsLoadValueMaximum] = useState(
    isLoadValueMaximumInitial
  );

  const setMaxAmountPerPeriod = (value: number | undefined) => {
    setValue('benefitSettings.lunch.maxAmountPerPeriod', value!, {
      shouldDirty: true
    });
    trigger('benefitSettings.lunch.maxAmountPerPeriod');
  };

  const handleOnChangeSpendingLimit = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(
      'benefitSettings.lunch.isMaximumSpendingLimit',
      event.target.value === LimitType.maximum,
      { shouldDirty: true }
    );
    if (event.target.value === LimitType.maximum) {
      setMaxAmountPerPeriod(maxValue);
      setIsLoadValueMaximum(true);
    } else {
      setIsLoadValueMaximum(false);
    }
  };

  useEffect(() => {
    setIsLoadValueMaximum(isLoadValueMaximumInitial);
    setMaxAmountPerPeriod(maxLunchGlobal);
  }, [disabled]);

  useEffect(() => {
    if (maxAmountPerPeriod === undefined) {
      setMaxAmountPerPeriod(maxValue);
    }
  }, [maxAmountPerPeriod]);

  return (
    <Box display="flex" flexDirection="row" gap={3} position="relative">
      <Box width="50%">
        <Box display="flex" alignItems="center" mb={1}>
          <Box
            color={disabled ? gray50 : gray85}
            fontSize={sizeSmall}
            fontWeight={fontSemiBold}
            id="lounari-loading-type-title"
          >
            {t('components.manage_benefits.spending_limit_field.title')}
          </Box>

          <BenefitSettingsTooltip
            descriptionKey={
              'components.manage_benefits.spending_limit_field.info'
            }
          />
          {isOnBoardingActive && (
            <Box position="absolute" top={-10} left={245}>
              <GuideDot
                storageKey="spending-limit-guide-dot"
                tooltipDescription={t('guided_tour.spending_limit')}
              />
            </Box>
          )}
        </Box>
        <RadioGroup
          onChange={handleOnChangeSpendingLimit}
          value={isLoadValueMaximum ? LimitType.maximum : LimitType.custom}
        >
          <FormControlLabel
            value={LimitType.maximum}
            control={<Radio />}
            label={t(
              'components.manage_benefits.spending_limit_field.labels.maximum'
            )}
            disabled={disabled}
          />
          <FormControlLabel
            value={LimitType.custom}
            control={<Radio />}
            label={t(
              'components.manage_benefits.spending_limit_field.labels.custom'
            )}
            disabled={disabled}
          />
        </RadioGroup>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          color={disabled ? gray50 : gray85}
        >
          <Box width="220px">
            <DailyLimitField
              label={t(`components.manage_benefit_lounari.detail_limit_label`)}
              readOnly={isLoadValueMaximum}
              benefitType={LUNCH.toLowerCase()}
              options={[]}
              name={`benefitSettings.lunch.maxAmountPerPeriod`}
              fundsLoadingTypeName={`benefitSettings.lunch.fundsLoadingType`}
              maxAmounts={[]}
              disabled={disabled}
              customColor={disabled ? gray50 : gray85}
              fullWidth={false}
              currentMaxAmount={maxLunchGlobal}
              helperText={
                isUserImpersonate
                  ? ''
                  : t('components.manage_benefit_lunch.lowering_hint')
              }
            />
          </Box>
          <BenefitSettingsTooltip
            descriptionKey={`components.manage_benefit_lunch.detail_daily_limit_statement`}
          />
        </Box>
      </Box>
      <Box width="50%">
        <Box display="flex" alignItems="center" mb={1}>
          <Box
            fontSize={sizeSmall}
            fontWeight={fontSemiBold}
            color={disabled ? gray50 : gray85}
            id="lounari-loading-type-title"
          >
            {t('components.manage_benefits.loading_type_field.title')}
          </Box>

          <BenefitSettingsTooltip
            descriptionKey={
              'components.manage_benefits.loading_type_field.lunch_info'
            }
          />
          {isOnBoardingActive && (
            <Box position="absolute" top={-10} left={870}>
              <GuideDot
                storageKey="load-type-guide-dot"
                tooltipDescription={t('guided_tour.load_type')}
              />
            </Box>
          )}
        </Box>
        <FundsLoadingType
          disabled={disabled}
          fundsLoadingType={fundsLoadingType}
          hasScheduledLoads={hasScheduledLoads}
          handleCloseModal={handleCloseModal}
        />
        <GeneralSettingsLoadTypeNotification
          isHidden={canApplyFundsLoadingChangeImmediately}
        />
      </Box>
    </Box>
  );
};

export { GeneralSettings };
